<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { breakpointsTailwind, useBreakpoints, useElementHover } from '@vueuse/core'
import { useElementTouch } from '~/composables/useElementTouch'
import { useActiveCardStore } from '~/stores/activeCard'
import { type Elemental, ElementalTypeEnum } from '@/types/elemental'
import type { List } from '~/types/list'
import { PriceType } from '@/types/api'
import ElemetalIconPicker from '~/components/Elemental/ElemetalIconPicker/index.vue'
import { capitalizeFirstLetter, formatNumberRounded, formatThousands } from '~/utils/formatting'

const props = defineProps<{
  elemental: Elemental | List
  isFeatured?: boolean
}>()

const isMobile = useBreakpoints(breakpointsTailwind).smaller('md')

const { elemental } = toRefs(props)
const { colors } = useTheme()
const showFallbackImage = ref(false)

const UpvoteCardButton = defineAsyncComponent(() => import('@/components/Elemental/Upvote/index.vue'))
const SaveButton = defineAsyncComponent(() => import('@/components/Elemental/SaveOnList/SaveElementalInAList.vue'))

const elementalTypeMetadata = getElementalMetadata(getElementalType(elemental.value.elementalTypeId, elemental.value.isList))
const endpoint = computed(() => {
  if (elemental.value.isList)
    return 'list'
  return elementalTypeMetadata?.name
})

const hasDiscount = computed(() => {
  return (elemental.value.discountPercentual ?? 0) > 0
})

const cardId = computed(() => elemental.value.url || Math.random().toString(36).substring(2))

const linkEl = ref<InstanceType<typeof NuxtLink> | null>(null)
const elementRef = computed(() => linkEl.value?.$el as HTMLElement | null)

const activeCardStore = useActiveCardStore()
const isTouchedLocal = isMobile.value ? useElementTouch(elementRef) : ref(false)
const isHover = ref(false)

if (process.client && !isMobile.value) {
  onMounted(() => {
    if (elementRef.value) {
      const hoverState = useElementHover(elementRef)
      watch(hoverState, (newValue) => {
        isHover.value = newValue
        if (newValue)
          activeCardStore.setActiveCard(cardId.value)
      })
    }
  })
}

if (process.client && isMobile.value) {
  onMounted(() => {
    if (elementRef.value) {
      elementRef.value.addEventListener('touchstart', () => {
        activeCardStore.setActiveCard(cardId.value)
      })
    }
  })
}

const isActive = computed(() => {
  if (isMobile.value)
    return isTouchedLocal.value && activeCardStore.activeCardId === cardId.value

  return isHover.value
})

const coverImage = computed(() => {
  if (elemental.value.images?.cover && elemental.value.images.cover?.[0]?.url)
    return optimizeImage(elemental.value.images.cover[0].url, { width: 450 })

  if (elemental.value.avatar)
    return optimizeImage(elemental.value.avatar, { width: 450 })

  if (elemental.value.images?.output && elemental.value.images.output?.[0]?.url)
    return optimizeImage(elemental.value.images.output[0].url, { width: 450 })

  const imageStyle = 'initials'
  const fontSize = 28
  const backgroundColors = ['F499D5', 'D496F1', 'BABAFF', '858AFE', 'D8F0BA', 'D7F7D0', 'D3F2FF']
  return `https://api.dicebear.com/9.x/${imageStyle}/svg?seed=${encodeURIComponent(props.elemental.title)}&fontSize=${fontSize}&backgroundColor=${backgroundColors.join(',')}&backgroundType=solid&shape1=d,e,g,i,n,r,t,a`
})

const showSkeleton = !process.client
const colorsElemental = getElementTypeColor(elemental.value.elementalTypeId || 1)
</script>

<template>
  <NuxtLink ref="linkEl" :to="`/${endpoint}/${elemental.url}`" class="flex flex-col gap-1 md:gap-2 group transition-all">
    <div class="relative rounded-2" :class="{ 'p-[2px]': hasDiscount }">
      <div v-if="hasDiscount" :class="$style['discount-badge']">
        <div :class="$style['discount-badge-text']">
          <span>{{ elemental.discountPercentual || 0 }}%</span>
          <span>off</span>
        </div>
      </div>

      <template v-if="showSkeleton">
        <AppPlaceholder
          width="100%"
          height="100%"
          class="w-full max-w-[272px] max-h-[182px] min-h-[110px] object-cover object-center aspect-3/2 rounded-2 bg-grayLighter"
        />
      </template>

      <ClientOnly>
        <img
          v-if="!showFallbackImage"
          loading="lazy"
          :src="coverImage"
          :alt="elemental.title"
          class="w-full min-w-[166px] min-h-[110px] object-cover object-center aspect-3/2 rounded-2 bg-grayLighter"
          @error="(e) => {
            showFallbackImage = true
            console.log('showFallbackImage', showFallbackImage)
          }"
        >

        <div
          v-if="showFallbackImage"
          class="w-full min-w-[166px] text-sm uppercase font-bold min-h-[110px] object-cover object-center aspect-3/2 rounded-2 bg-grayLighter flex items-center justify-center select-none"
          :style="{ backgroundColor: colorsElemental[1], color: colorsElemental[0] }"
        >
          {{ elementalTypeMetadata?.name || 'Elemental' }}
        </div>
      </ClientOnly>

      <div v-if="!isFeatured && elemental.isFeatured" class="absolute left-2 bottom-2 px-2 py-1 bg-white/80 rounded-1">
        <Text variant="caption" bold :is-gradient-color="true">
          Featured
        </Text>
      </div>
      <div
        v-else
        class="absolute left-2 bottom-2 py-[3px] pl-[4px] pr-[6px] flex h-[20px] gap-0 items-center bg-white/80 rounded-1 backdrop-blur-[8px] drop-shadow-[0_0_4px_rgba(0,0,0,0.05)] font-bold leading-none text-[10px] color-softBlack"
      >
        <ElemetalIconPicker v-if="elementalTypeMetadata" :type="elementalTypeMetadata?.id" class="w-4 h-4" />
        <TextV2>{{ capitalizeFirstLetter(elementalTypeMetadata?.name) }}</TextV2>
      </div>

      <ClientOnly>
        <div class="right-2 top-2" :class="[$style.float, { invisible: !isActive }]">
          <UpvoteCardButton :element="elemental" />
        </div>
        <div class="right-2 bottom-2" :class="[$style.float, { invisible: !isActive }]">
          <SaveButton
            :elemental-type="elemental.isList ? ElementalTypeEnum.List : elemental.elementalTypeId"
            :elemental="elemental"
            :class="$style.btn"
          />
        </div>
      </ClientOnly>
    </div>

    <div class="flex flex-col gap-0.5">
      <TextV2 class="text-[14px] <md:text-[12px] font-bold truncate" :title="elemental.title">
        {{ elemental.title }}
      </TextV2>

      <div class="flex items-center gap-[5px] md:gap-[10px] min-h-[24px]">
        <div class="overflow-hidden">
          <UserChip variant="text" :color="colors.softBlack" :user="elemental.user" />
        </div>

        <div class="flex items-center gap-1 flex-shrink-0 ml-auto">
          <TextV2
            v-if="elemental.nSaves"
            class="flex items-center text-[10px] text-softBlack font-bold gap-[2px] flex-shrink-0"
          >
            <div i-material-symbols:bookmark-outline />
            <Text variant="caption" bold>
              {{ formatThousands(elemental.nSaves) }}
            </Text>
          </TextV2>

          <TextV2
            v-if="elemental.nUses"
            class="flex items-center text-[10px] text-softBlack font-bold gap-[2px] flex-shrink-0"
          >
            <div i-ic:outline-arrow-outward />
            <Text variant="caption" bold>
              {{ formatThousands(elemental.nUses) }}
            </Text>
          </TextV2>
        </div>

        <div v-if="elemental.isPremium" class="flex flex-shrink-0">
          <div
            class="flex-inline center px-1 sm:px-1 bg-white min-w-[36px] h-[24px] border-1 rounded-[3px] flex-shrink-0"
          >
            <TextV2 class="flex items-center font-bold text-[10px] text-softBlack font-bold gap-[2px]">
              <span v-if="elemental.priceTypeId === PriceType.fairPrice || elemental.price === undefined">
                Fair-price
              </span>
              <template v-else>
                <span
                  v-if="elemental.priceOriginal && elemental.priceOriginal > elemental.price"
                  class="line-through mr-1 opacity-75"
                >
                  ${{ formatNumberRounded((elemental.priceOriginal || 0) / 100) }}
                </span>
                <span>${{ formatNumberRounded((elemental.price || 0) / 100) }}</span>
              </template>
            </TextV2>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style lang="scss" module>
.discount-badge {
  @apply absolute bg-primary;
  left: 0;
  top: 0;
  --r: 6px;
  width: 60px;
  aspect-ratio: 1;
  border-top-left-radius: var(--r);
  clip-path: polygon(0 100%, 100% 0, 0 0);
  --_g: calc(50% * (1 + 1 / tan(22.5deg)) - var(--r) / (3 * sqrt(2) - 4));
  --_r: calc(var(--r) / tan(22.5deg)), #000 98%, #0000 101%;
  -webkit-mask:
    radial-gradient(var(--r) at top var(--r) right var(--_r)),
    radial-gradient(var(--r) at left var(--r) bottom var(--_r)),
    conic-gradient(from -67.5deg at var(--_g) var(--_g), #000 45deg, #0000 0);
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.05);
  user-select: none;
}

.discount-badge-text {
  @apply color-softBlack;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  line-height: 1em;
  display: flex;
  flex-direction: column;
  text-align: center;
  width: 30px;
  height: 20px;
  transform: translateX(2px) translateY(7px) rotate(-45deg);
}

.float {
    @apply absolute;
    &:has(.btn[aria-expanded='true']) {
      @apply visible;
    }
}
</style>
